import { AxiosError } from 'axios';

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Address } from 'viem';

import { getApiMethod, postApiMethod } from '@helpers/common/axios.helper';
import { parseAxiosError } from '@helpers/common/error.helpers';

import { removeUndefinedValues } from '@utils/common.utils';

import showToast from '@components/common/Toast';

import { VOTING_API_URL } from '@constants/common/api.constants';
import {
  ErrorMessages,
  SuccessMessages,
} from '@constants/common/messages.constants';
import {
  INITIAL_PAGE,
  PAGINATION_FARM_STEP_ITEMS,
} from '@constants/common/pagination.constants';
import { ToastType } from '@constants/common/toast.constants';
import {
  API_VOTING,
  API_VOTING_CHAINS,
  API_VOTING_DEXES,
  API_VOTING_FARM,
  API_VOTING_FARMS,
  API_VOTING_STATISTICS,
} from '@constants/voting/api.constants';

import {
  FarmDataDto,
  FarmsStatisticsDto,
  VotingDto,
} from '@dto/vaults/voting.dto';

const getMethod = getApiMethod(VOTING_API_URL as string, false);

const postMethod = postApiMethod(VOTING_API_URL as string);

// ----------------------------------------------getVotingDexes-----------------------------------------------------------

export const getVotingDexes = () => getMethod<string[]>(API_VOTING_DEXES);

export const useGetVotingDexes = () =>
  useQuery<string[]>({
    queryKey: [API_VOTING_DEXES],
    queryFn: () => getVotingDexes(),
    retry: false,
  });

// ----------------------------------------------getVotingChainId-----------------------------------------------------------

export const getVotingChainId = () => getMethod<string[]>(API_VOTING_CHAINS);

export const useGetVotingChainId = () =>
  useQuery<string[]>({
    queryKey: [API_VOTING_CHAINS],
    queryFn: () => getVotingChainId(),
    retry: false,
  });

// ----------------------------------------------getVotingFarms-----------------------------------------------------------

interface VotingFarmsParams {
  isActive: boolean;
  chains?: string[];
  dexes?: string[];
  search?: string;
  sortBy?: string;
  sortDirection?: string;
  account?: string;
  skip?: number;
}

export const getVotingFarms = (params: VotingFarmsParams) =>
  getMethod<VotingDto>(API_VOTING_FARMS, {
    ...params,
    limit: PAGINATION_FARM_STEP_ITEMS,
  });

export const useVotingFarms = (params: VotingFarmsParams) => {
  const filteredParams = removeUndefinedValues(params);

  return useInfiniteQuery({
    queryKey: [API_VOTING_FARMS, filteredParams],
    queryFn: ({ pageParam }) =>
      getVotingFarms({
        skip: pageParam * PAGINATION_FARM_STEP_ITEMS,
        ...filteredParams,
      }),
    initialPageParam: INITIAL_PAGE,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.items.length > 0 ? allPages.length : undefined,
    refetchOnMount: false,
  });
};

// ----------------------------------------------getFarmsStatistics-----------------------------------------------------------

interface StatisticsFarmsParams {
  chains?: string[];
  dexes?: string[];
  sortBy?: string;
  sortDirection?: string;
  skip?: number;
}

export const getFarmsStatistics = (params: StatisticsFarmsParams) =>
  getMethod<FarmsStatisticsDto>(API_VOTING_STATISTICS, {
    ...params,
    limit: PAGINATION_FARM_STEP_ITEMS,
  });

export const useFarmStatistics = (params: StatisticsFarmsParams) => {
  const filteredParams = removeUndefinedValues(params);

  return useInfiniteQuery({
    queryKey: [API_VOTING_STATISTICS, filteredParams],
    queryFn: ({ pageParam }) =>
      getFarmsStatistics({
        skip: pageParam * PAGINATION_FARM_STEP_ITEMS,
        ...filteredParams,
      }),
    initialPageParam: INITIAL_PAGE,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.items.length > 0 ? allPages.length : undefined,
    refetchOnMount: false,
  });
};

// ----------------------------------------------activeVoteFarm-----------------------------------------------------------

const activeVoteFarm = (
  params: {
    data: { timestamp: number; account: string };
    signature: string;
  },
  id: number,
): Promise<void> => postMethod(`${API_VOTING}/${id}/vote`, params);

export const useActiveVoteFarm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      params,
      id,
    }: {
      params: {
        data: { timestamp: number; account: string };
        signature: string;
      };
      id: number;
    }) => activeVoteFarm(params, id),
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VOTING_FARMS] }),
      ]);
      showToast({ title: SuccessMessages.Voted }, ToastType.Success);
    },
  });
};

// ----------------------------------------------getVotingFarm-----------------------------------------------------------

interface VoteFarmParams {
  account: string;
  link: string;
}

const getVotingFarm = (params: VoteFarmParams): Promise<FarmDataDto> =>
  postMethod(API_VOTING_FARM, { ...params });

export const useVoteFarm = () => {
  return useMutation({
    mutationFn: getVotingFarm,
    onError: (error) => {
      const message = parseAxiosError(error as AxiosError);

      if (
        message === ErrorMessages.IncorrectLink ||
        message === ErrorMessages.PairNotExists
      )
        return;
      showToast({ title: message }, ToastType.Error);
    },
  });
};

// ----------------------------------------------createProposal-----------------------------------------------------------

interface CreateProposalParams {
  account: Address;
  id: number;
}

const createProposal = ({
  account,
  id,
}: CreateProposalParams): Promise<FarmDataDto> =>
  postMethod(`${API_VOTING}/${id}/activate`, { account });

export const useCreateProposal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProposal,
    onError: (error) => {
      showToast(
        { title: parseAxiosError(error as AxiosError) },
        ToastType.Error,
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: [API_VOTING_FARMS] }),
        queryClient.refetchQueries({ queryKey: [API_VOTING_DEXES] }),
        queryClient.refetchQueries({ queryKey: [API_VOTING_CHAINS] }),
      ]);
      showToast({ title: SuccessMessages.Created }, ToastType.Success);
    },
  });
};
